/**
 * Shared dashboard types + helpers.
 *
 * The three dashboard variants (Console, Warm, Depth) all consume the SAME
 * payload: one `useQuery(['dashboard'])` in the page shell, passed down as a
 * prop. Keeping the interface here — not duplicated per variant — is what makes
 * them pure presentational components over one source of truth.
 */

import type { Tone } from '../../components/ui';

export interface DashboardData {
  organization: { id: string; name: string; tier: string; plan: string; planExpiresAt: string | null };
  overallCompliance: number | null;
  assessedFrameworks: number;
  licensedFrameworks: number;
  frameworks: Array<{
    key: string; name: string; version: string; licenseExpiresAt: string;
    submissionStatus: string | null; complianceRate: number | null;
    answeredCount: number; totalCount: number; submissionId: string | null;
  }>;
  gaps: {
    total: number;
    byStatus: Record<string, number>;
    bySeverity: Record<string, number>;
    byFramework: Record<string, number>;
    byPlanMembership: Record<string, number>;
  };
  plans: {
    total: number; open: number; completed: number; cancelled: number;
    atRisk: number; overdue: number; highCritical: number;
    byStatus: Record<string, number>; byPriority: Record<string, number>;
  };
  credits: { balance: number };
  policies: { active: number; total: number };
  vendors: { active: number };
}

export interface DashboardVariantProps {
  data: DashboardData;
}

/**
 * Verdict tone for a 0–100 compliance rate. Shared so "27%" reads the same
 * severity in every variant. This is the STATUS judgement; the per-direction
 * accent (ochre / indigo) is identity and never stands in for it.
 */
export function toneForRate(rate: number): Tone {
  return rate >= 80 ? 'good' : rate >= 50 ? 'serious' : 'critical';
}

/** Two-letter identity token from a name, e.g. "Helios Energy SA" -> "HE". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}
